import {DatePicker} from '@github-ui/date-picker'
import {useCallback, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import {commitClearProjectItemFieldValueMutation} from '../../../mutations/clear-project-item-field-value'
import {commitUpdateProjectItemFieldValueMutation} from '../../../mutations/update-project-item-field-value'
import {formatDateString} from '../../../utils/date-formatting'
import type {ProjectDateFieldValueNode, ProjectFieldNode} from '../project-item-section-fields-query-types'
import {FieldWrapper} from './FieldWrapper'
import type {BaseFieldProps} from './Shared'

export type DateFieldConfigFragment = Pick<ProjectFieldNode, 'id' | 'name'>

export type DateFieldFragment = Pick<ProjectDateFieldValueNode, 'id' | 'date'>

type DateFieldProps = BaseFieldProps<DateFieldConfigFragment, DateFieldFragment>

export function DateField({
  viewerCanUpdate,
  itemId,
  projectId,
  field,
  value: valueData,
  onIssueUpdate,
}: DateFieldProps) {
  const environment = useRelayEnvironment()

  const fieldId = field.id
  const fieldName = field.name

  const valueDataAsDate = valueData?.date ? new Date(valueData.date) : null
  const currentDisplayValue = valueDataAsDate ? formatDateString(valueDataAsDate) : 'No date'

  const [currentValue, setCurrentValue] = useState<Date | null>(valueDataAsDate)
  const [showInput, setShowInput] = useState(false)

  const onDatePickerChange = useCallback(
    (date: Date | null) => {
      if (!fieldId) return
      setCurrentValue(date)
      if (!date) {
        commitClearProjectItemFieldValueMutation({
          environment,
          input: {
            fieldId,
            itemId,
            projectId,
          },
          onCompleted: onIssueUpdate,
        })
      } else {
        // convert to iso format, removing the timezone offset
        const isoDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString()
        commitUpdateProjectItemFieldValueMutation({
          environment,
          input: {
            fieldId,
            itemId,
            projectId,
            value: {date: isoDateTime.slice(0, 10)},
          },
          onCompleted: onIssueUpdate,
        })
      }
    },
    [environment, fieldId, itemId, onIssueUpdate, projectId],
  )

  if (!fieldName) return null

  if (!viewerCanUpdate)
    return (
      <FieldWrapper
        showInput={showInput}
        setShowInput={setShowInput}
        placeholder={LABELS.emptySections.noValue(fieldName)}
        canUpdate={false}
        name={fieldName}
        value={currentDisplayValue}
      />
    )

  return (
    <DatePicker
      variant="single"
      showClearButton
      value={currentValue}
      onChange={onDatePickerChange}
      anchor={(anchorProps: React.HTMLAttributes<HTMLElement>) => {
        return (
          <FieldWrapper
            showInput={showInput}
            setShowInput={setShowInput}
            anchorProps={anchorProps}
            placeholder={LABELS.emptySections.datePlaceholder}
            value={currentDisplayValue || LABELS.emptySections.noValue(fieldName)}
            name={fieldName}
          />
        )
      }}
    />
  )
}
